import { Centrifuge, type SubscribedContext } from 'centrifuge'
import loggerService, { type LoggerService } from '@widgets/shared/services/loggerService'
import ConfigReader from '@/core/configReader'
import type { CentrifugeSubscriptionToken, CentrifugeTokensResponse } from '@/services/domain/accounts/types'
import type {
  EventToPayloadAbstractMap,
  HandlerOfAllEvents,
  ITypedWebSocketService,
  SocketEventsUnionFromMap,
} from '@/services/transport/types'

type WebSocketInterface<EventMap extends EventToPayloadAbstractMap> = Omit<
  ITypedWebSocketService<EventMap>,
  'addDefaultChannelListener' | 'removeDefaultChannelListener' | 'removeAllDefaultChannelListener'
>

export class WebSocketService<EventMap extends EventToPayloadAbstractMap> implements WebSocketInterface<EventMap> {
  private loggerService: LoggerService

  private centrifugeTokens: CentrifugeTokensResponse | undefined

  private connection: Centrifuge | null = null

  private connectionUrl: string

  private defaultChannelListeners: HandlerOfAllEvents<EventMap>[] = []

  private channelListeners: { [channel: string]: HandlerOfAllEvents<EventMap>[] } = {}

  private statusChangeListeners: ((isConnected: boolean) => void)[] = []

  private subscriptionInitListeners: ((ctx: SubscribedContext) => void)[] = []

  constructor() {
    this.loggerService = loggerService
    this.connectionUrl = ConfigReader.config().socketUrl
  }

  public setCentrifugeTokens(centrifugeTokens: CentrifugeTokensResponse) {
    this.centrifugeTokens = centrifugeTokens
  }

  private getConnectionToken(): string | undefined {
    return this.centrifugeTokens?.connectionToken
  }

  public getSubscriptionToken(): CentrifugeSubscriptionToken | undefined {
    return this.centrifugeTokens?.subscriptionTokens[0]
  }

  public connect() {
    if (this.connection?.state === 'connected') {
      return
    }
    if (this.connection) {
      this.connection.connect()
      return
    }
    const subscriptionToken = this.getSubscriptionToken()
    if (!subscriptionToken) {
      throw new Error('No subscription token')
    }
    try {
      this.connection = new Centrifuge(this.connectionUrl, {
        token: this.getConnectionToken(),
      })
      const sub = this.connection.newSubscription(subscriptionToken.channel, {
        token: subscriptionToken.subscriptionToken,
      })

      this.connection.once('connected', () => {
        this.loggerService.log(`WebSocket connected with config: ${JSON.stringify(this.connectionUrl)}`)
      })
      this.connection.on('error', (ctx) => {
        if (ctx.error.code === 2) {
          this.statusChangeListeners.forEach((cb) => cb(false))
          return
        }
        this.loggerService.logError('Web Socket error.')
        this.loggerService.logError(ctx.error?.message)
      })
      this.connection.on('disconnected', () => {
        this.statusChangeListeners.forEach((cb) => cb(false))
      })
      this.connection.on('connected', () => {
        this.statusChangeListeners.forEach((cb) => cb(true))
      })
      sub.on('publication', (ctx: SocketEventsUnionFromMap<EventMap>) => {
        const channelCallbacks = this.channelListeners[ctx.channel]
        if (channelCallbacks) {
          channelCallbacks.forEach((cb) => cb(ctx.data))
        } else {
          this.defaultChannelListeners.forEach((cb) => cb(ctx.data))
        }
      })
      sub.on('subscribed', (ctx: SubscribedContext) => {
        this.subscriptionInitListeners.forEach((cb) => cb(ctx))
      })

      sub.subscribe()
      this.connection.connect()
    } catch (e) {
      if (e instanceof Error) {
        this.loggerService.logError('Cannot connect to websocket')
      }
    }
  }

  protected getConnection() {
    if (!this.connection) {
      throw Error('WebSocket connection not established')
    }
    return this.connection
  }

  public hasConnection() {
    return !!this.connection
  }

  public disconnect() {
    const connection = this.getConnection()
    try {
      if (connection.state === 'disconnected') {
        return
      }
      connection.disconnect()
      this.connection = null
    } finally {
      connection.removeAllListeners()
    }
  }

  public isConnected() {
    return !!this.connection && this.connection.state === 'connected'
  }

  public addEventListener(channel: string, handler: HandlerOfAllEvents<EventMap>) {
    let channelCallbacks = this.channelListeners[channel]
    if (!channelCallbacks) {
      channelCallbacks = []
      this.channelListeners[channel] = channelCallbacks
    }
    if (channelCallbacks.indexOf(handler) === -1) {
      channelCallbacks.push(handler)
    }
    if (!this.isConnected()) {
      this.connect()
    }
  }

  public removeEventListener(channel: string, handler: HandlerOfAllEvents<EventMap>) {
    const channelCallbacks = this.channelListeners[channel]
    if (!channelCallbacks) {
      return
    }
    const index = channelCallbacks.indexOf(handler)
    if (index >= 0) {
      channelCallbacks.splice(index, 1)
    }
  }

  public async publish(channel: string, data: unknown): Promise<void> {
    if (this.connection) {
      await this.connection.publish(channel, data)
    }
  }

  public removeAllEventListener(channel: string): void {
    delete this.channelListeners[channel]
  }

  public addChannelListener(handler: HandlerOfAllEvents<EventMap>): void {
    if (this.defaultChannelListeners.indexOf(handler) === -1) {
      this.defaultChannelListeners.push(handler)
    }
    if (!this.isConnected()) {
      this.connect()
    }
  }

  public removeChannelListener(handler: HandlerOfAllEvents<EventMap>): void {
    const index = this.defaultChannelListeners.indexOf(handler)
    if (index >= 0) {
      this.defaultChannelListeners.splice(index, 1)
    }
  }

  public removeAllChannelListener() {
    this.defaultChannelListeners = []
  }

  public addStatusChangeListener(handler: (isConnected: boolean) => void) {
    if (this.statusChangeListeners.indexOf(handler) === -1) {
      this.statusChangeListeners.push(handler)
    }
  }

  public removeStatusChangeListener(handler: (isConnected: boolean) => void) {
    const index = this.statusChangeListeners.indexOf(handler)
    if (index >= 0) {
      this.statusChangeListeners.splice(index, 1)
    }
  }

  public addSubscriptionInitListener(handler: (ctx: SubscribedContext) => void) {
    if (this.subscriptionInitListeners.indexOf(handler) === -1) {
      this.subscriptionInitListeners.push(handler)
    }
  }

  public removeSubscriptionInitListener(handler: (ctx: SubscribedContext) => void) {
    const index = this.subscriptionInitListeners.indexOf(handler)
    if (index >= 0) {
      this.subscriptionInitListeners.splice(index, 1)
    }
  }

  public removeAllListeners() {
    this.defaultChannelListeners = []
    this.channelListeners = {}
    this.statusChangeListeners = []
    this.subscriptionInitListeners = []
  }
}

export default new WebSocketService()
